/**
 * Detecção de navegador sem suporte.
 *
 * O aplicativo roda sobre React 19 e AWS Amplify, que assumem APIs de
 * JavaScript/DOM modernas. Navegadores antigos (ex.: Chrome 79, de 2019)
 * não possuem `Array.prototype.at` nem `AbortSignal.timeout`, o que provoca
 * erros de inicialização opacos como "this.o.at is not a function" e deixa
 * a tela em branco. Em vez de quebrar silenciosamente, detectamos a ausência
 * dessas capacidades e exibimos um aviso amigável pedindo a atualização.
 *
 * `AbortSignal.timeout` (Chrome 103+, Firefox 100+, Safari 16+) é um bom
 * indicador de modernidade: se existir, o navegador é recente o suficiente
 * para o restante do aplicativo.
 *
 * IMPORTANTE: este arquivo faz parte do "gate" que roda ANTES de qualquer
 * código do app. Ele precisa ser analisável por navegadores antigos, então
 * NÃO use sintaxe ES2020+ (optional chaining `?.`, nullish coalescing `??`),
 * pois o esbuild não as transpila no target padrão e um SyntaxError aqui
 * impediria até a tela de aviso de aparecer. Cores em hex (não `oklch`),
 * pelo mesmo motivo.
 */

/**
 * Retorna `true` se o navegador possui as APIs mínimas necessárias para
 * carregar o aplicativo com segurança.
 */
export const isBrowserSupported = (): boolean => {
  try {
    const hasArrayAt = typeof Array.prototype.at === 'function';
    const hasAbortTimeout =
      typeof AbortSignal !== 'undefined' &&
      typeof (AbortSignal as unknown as { timeout?: unknown }).timeout === 'function';

    return hasArrayAt && hasAbortTimeout;
  } catch {
    return false;
  }
};

// Cores em hex equivalentes aos tokens da marca (packages/themes/precisa.css),
// pois navegadores antigos não interpretam `oklch`:
//   background #efeae6 · card #f4f1ee · border #ddd2c8 · foreground #231b3c
//   muted-foreground #6e6e8e · primary #463c6d · accent #e1e1ff
const BROWSER_LINKS = [
  { label: 'Google Chrome', url: 'https://www.google.com/chrome/' },
  { label: 'Microsoft Edge', url: 'https://www.microsoft.com/edge' },
  { label: 'Mozilla Firefox', url: 'https://www.mozilla.org/firefox/new/' },
  { label: 'Safari', url: 'https://www.apple.com/safari/' },
];

const browserPill = (label: string, url: string): string =>
  `<a href="${url}" target="_blank" rel="noopener noreferrer" style="background:#e1e1ff;color:#463c6d;border-radius:999px;padding:6px 14px;font-size:13px;font-weight:600;text-decoration:none;">${label}</a>`;

const UNSUPPORTED_HTML = `
  <div style="
    position: fixed;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    background: #efeae6;
    font-family: system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
    color: #231b3c;
  ">
    <div style="
      max-width: 440px;
      width: 100%;
      background: #f4f1ee;
      border: 1px solid #ddd2c8;
      border-radius: 16px;
      padding: 40px 32px;
      text-align: center;
    ">
      <svg
        aria-hidden="true"
        fill="#463c6d"
        fill-rule="evenodd"
        viewBox="-46 267 969 969"
        style="width: 56px; height: 56px; margin-bottom: 24px;"
      >
        <g transform="matrix(4.166667,0,0,4.166667,-1513.302,-909)">
          <path
            d="M460.411 435.128c-21.211-8.385-44.654-20.828-52.035-44.37-1.49-4.832-2.32-2.728-3.237 1.026-12.309 43.08.491 101.357 50.583 106.243 11.43.984 24.006-4.632 28.734-15.457 9.12-20.011-4.005-41.99-23.808-48.347l-.237-.095z"
            fill-rule="nonzero"
          />
          <path
            d="M479.026 299.26c-2.207-.327-4.42-.529-6.618-.598-15.131-.468-58.156-.074-64.872-.008-.496.004-.886.408-.886.905v60.679c0 .499.404.904.903.905l62.037.117c.499.001.903.405.903.905l.005 60.938c0 .519.44.94.957.914 4.34-.219 8.544-.879 12.587-1.908 27.979-6.146 48.924-31.066 48.924-60.891 0-18.962-8.854-37.194-23.733-48.944-8.5-6.712-19.298-11.395-30.208-13.014"
            fill-rule="nonzero"
          />
        </g>
      </svg>
      <h1 style="margin: 0 0 12px; font-size: 22px; font-weight: 600; line-height: 1.3;">
        Navegador não compatível
      </h1>
      <p style="margin: 0 0 16px; font-size: 15px; line-height: 1.6; color: #6e6e8e;">
        Este aplicativo precisa de um navegador mais recente para funcionar
        com segurança. O seu está desatualizado e não consegue carregá-lo.
      </p>
      <p style="margin: 0 0 20px; font-size: 15px; line-height: 1.6; color: #6e6e8e;">
        Para continuar, atualize o seu navegador ou utilize a versão mais
        recente de um destes:
      </p>
      <div style="
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        justify-content: center;
      ">
        ${BROWSER_LINKS.map((b) => browserPill(b.label, b.url)).join('')}
      </div>
    </div>
  </div>
`;

const LOAD_ERROR_HTML = `
  <div style="
    position: fixed;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    background: #efeae6;
    font-family: system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
    color: #231b3c;
  ">
    <div style="
      max-width: 440px;
      width: 100%;
      background: #f4f1ee;
      border: 1px solid #ddd2c8;
      border-radius: 16px;
      padding: 40px 32px;
      text-align: center;
    ">
      <h1 style="margin: 0 0 12px; font-size: 22px; font-weight: 600; line-height: 1.3;">
        Não foi possível carregar
      </h1>
      <p style="margin: 0 0 24px; font-size: 15px; line-height: 1.6; color: #6e6e8e;">
        Ocorreu um erro ao carregar o aplicativo. Verifique a sua conexão e
        tente novamente.
      </p>
      <button
        type="button"
        onclick="location.reload()"
        style="background:#463c6d;color:#ffffff;border:none;border-radius:999px;padding:10px 24px;font-size:14px;font-weight:600;cursor:pointer;"
      >
        Recarregar
      </button>
    </div>
  </div>
`;

/**
 * Remove a splash inline e substitui o conteúdo da página por uma tela
 * estática (sem React). O fallback para `document.body` é defensivo: o
 * `#root` está sempre presente em index.html, então não deve disparar.
 */
const renderScreen = (html: string, title: string): void => {
  const splash = document.getElementById('splash');
  if (splash) {
    splash.remove();
  }

  const root = document.getElementById('root');
  const target = root !== null ? root : document.body;
  target.innerHTML = html;

  document.title = title;
};

/**
 * Substitui o conteúdo da página por um aviso de navegador incompatível,
 * evitando que o aplicativo React seja montado.
 */
export const renderUnsupportedBrowser = (): void => {
  renderScreen(UNSUPPORTED_HTML, 'Navegador não compatível — Precisa Saúde');
};

/**
 * Substitui o conteúdo da página por uma tela de erro de carregamento, com
 * botão para recarregar. Usada quando o chunk do aplicativo falha ao carregar
 * (deploy desatualizado, falha de rede), evitando a splash girando para sempre.
 */
export const renderLoadError = (): void => {
  renderScreen(LOAD_ERROR_HTML, 'Não foi possível carregar — Precisa Saúde');
};
